import React from 'react';
import {
    Create,
    SimpleForm,
    TextInput,
    required,
    useTranslate,
    useNotify,
    useRedirect,
    Toolbar,
    SaveButton,
} from 'react-admin';
import { Box, Card, CardContent, Typography, IconButton } from '@mui/material';
import { ArrowBack, EmojiTransportation } from '@mui/icons-material';

const transformBrandSubmission = (data: any) => {
    const translations: Record<string, string> = {};
    if (data.translation_en) translations.en = data.translation_en;
    if (data.translation_zh_tw) translations['zh-TW'] = data.translation_zh_tw;
    if (data.translation_zh_cn) translations['zh-CN'] = data.translation_zh_cn;
    return { translations };
};

const CreateToolbar = () => (
    <Toolbar
        sx={{
            backgroundColor: '#FAFAFA',
            borderTop: '1px solid #E0E0E0',
            px: { xs: 2, sm: 3 },
            py: 2,
            mt: { xs: 2, sm: 4 },
            '& .MuiButton-root': {
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                py: 1.5,
            },
        }}
    >
        <SaveButton
            variant="contained"
            sx={{
                backgroundColor: '#00C853',
                '&:hover': { backgroundColor: '#00A843' },
            }}
        />
    </Toolbar>
);

const PageHeader = () => {
    const translate = useTranslate();
    const redirect = useRedirect();
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: { xs: 2, sm: 4 } }}>
            <IconButton
                onClick={() => redirect('/vehicle-brands')}
                size="large"
                sx={{
                    backgroundColor: '#FFFFFF',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                }}
                title={translate('ra.action.back')}
            >
                <ArrowBack />
            </IconButton>
            <Box>
                <Typography sx={{ fontWeight: 600, mb: 0.5, fontSize: { xs: '1.25rem', sm: '2.125rem' } }}>
                    {translate('resources.vehicle-brands.create.title', { _: 'Create Vehicle Brand' })}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    {translate('resources.vehicle-brands.create.subtitle', {
                        _: 'Add a new vehicle brand with translations',
                    })}
                </Typography>
            </Box>
        </Box>
    );
};

export const VehicleBrandsCreate = () => {
    const translate = useTranslate();
    const notify = useNotify();
    const redirect = useRedirect();

    return (
        <Create
            title={false}
            component="div"
            redirect="list"
            transform={transformBrandSubmission}
            sx={{
                maxWidth: '100%',
                overflow: 'hidden',
                backgroundColor: 'transparent',
                '& .RaCreate-main': {
                    backgroundColor: 'transparent',
                    boxShadow: 'none',
                },
            }}
            mutationOptions={{
                onSuccess: () => {
                    notify('resources.vehicle-brands.notifications.created', {
                        type: 'success',
                        messageArgs: { _: 'Brand created' },
                    });
                    redirect('list', 'vehicle-brands');
                },
                onError: (error: any) => {
                    const code = error?.body?.error_code as string | undefined;
                    if (code === 'VEHICLE_BRAND_DUPLICATE') {
                        notify('resources.vehicle-brands.errors.duplicate', {
                            type: 'error',
                            messageArgs: { _: 'A brand with this name already exists' },
                        });
                    } else {
                        notify(error?.message || 'Failed to create brand', { type: 'error' });
                    }
                },
            }}
        >
            <SimpleForm
                toolbar={<CreateToolbar />}
                sx={{
                    '& .MuiPaper-root': {
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                    },
                    '& > .MuiCardContent-root, & > div > .MuiCardContent-root': {
                        px: { xs: 0, sm: 2 },
                        py: { xs: 1, sm: 2 },
                    },
                    padding: { xs: 0, sm: 'inherit' },
                }}
            >
                <Box sx={{ width: '100%', maxWidth: 800, margin: '0 auto', pb: { xs: 10, sm: 2 }, '& .MuiFormControl-root': { width: '100%' } }}>
                    <PageHeader />
                    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <EmojiTransportation sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    {translate('resources.vehicle-brands.sections.translations', {
                                        _: 'Brand name (per language)',
                                    })}
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'grid', gap: 2 }}>
                                <TextInput
                                    source="translation_en"
                                    label={translate('resources.vehicle-brands.fields.translationEn', {
                                        _: 'English Name',
                                    })}
                                    validate={required()}
                                    fullWidth
                                />
                                <TextInput
                                    source="translation_zh_tw"
                                    label={translate('resources.vehicle-brands.fields.translationZhTw', {
                                        _: '繁體中文 Name',
                                    })}
                                    fullWidth
                                />
                                <TextInput
                                    source="translation_zh_cn"
                                    label={translate('resources.vehicle-brands.fields.translationZhCn', {
                                        _: '简体中文 Name',
                                    })}
                                    fullWidth
                                />
                            </Box>
                        </CardContent>
                    </Card>
                </Box>
            </SimpleForm>
        </Create>
    );
};
